import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import ImageLightbox from '../components/ImageLightbox';
import { authHeaders } from './format';
import { Motion, presets } from '../motion';

// Chat attachments are only served with the session token, so <img src> can't load them directly.
// They are fetched as blobs and shown through object URLs, with a small cache.
// Where they come from is set by AttachmentSource: DM attachments from the Core (default),
// channel attachments from a community server with its node token.

const CACHE_LIMIT = 80;
const cache = new Map(); // cache key -> Promise<objectURL>

export const attachmentUrl = (id, thumb = false) => `/api/dm/attachments/${id}${thumb ? '?thumb=1' : ''}`;

const dmSource = {
  key: 'dm',
  fetchBlob: (id, thumb) => axios.get(attachmentUrl(id, thumb), { headers: authHeaders(), responseType: 'blob' }).then(res => res.data),
};
const SourceContext = createContext(dmSource);
// source: { key, fetchBlob(id, thumb) -> Promise<Blob> }
export const AttachmentSource = ({ source, children }) => <SourceContext.Provider value={source}>{children}</SourceContext.Provider>;
const useSource = () => useContext(SourceContext);

function fetchBlobUrl(source, id, thumb = false) {
  const url = `${source.key}:${id}:${thumb ? 't' : 'f'}`;
  if (cache.has(url)) {
    const hit = cache.get(url);
    cache.delete(url);
    cache.set(url, hit); // most recently used last
    return hit;
  }
  const promise = source.fetchBlob(id, thumb).then(blob => URL.createObjectURL(blob));
  promise.catch(() => cache.delete(url));
  cache.set(url, promise);
  while (cache.size > CACHE_LIMIT) {
    const [oldest, old] = cache.entries().next().value;
    cache.delete(oldest);
    old.then(u => URL.revokeObjectURL(u)).catch(() => {});
  }
  return promise;
}

// Object URL of an attachment (or its thumbnail); null while loading. enabled=false waits.
export function useAttachmentUrl(id, { thumb = false, enabled = true } = {}) {
  const source = useSource();
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!id || !enabled) return undefined;
    let alive = true;
    setError(false);
    fetchBlobUrl(source, id, thumb).then(u => { if (alive) setUrl(u); }).catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [id, thumb, enabled, source]);
  return { url, error };
}

export function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

export function formatDuration(ms) {
  if (!ms && ms !== 0) return '';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const KIND_LABEL = { image: ['Photo', 'photos'], video: ['Video', 'videos'], voice: ['Voice message', 'voice messages'], audio: ['Audio', 'audio files'], file: ['File', 'files'] };

// "Photo", "3 photos", "View-once video" (chat list previews)
export function attachmentLabel({ kind, count = 1, view_once: viewOnce }) {
  const [one, many] = KIND_LABEL[kind] || KIND_LABEL.file;
  if (viewOnce) return `View-once ${one.toLowerCase()}`;
  return count > 1 ? `${count} ${many}` : one;
}

/* ── Rendering inside a message ── */

// Photos and videos stand on their own (no bubble, like iMessage); everything else goes in the bubble.
// View-once media is never shown as a picture, so it counts as "rest".
export function splitAttachments(attachments = []) {
  if (attachments[0]?.view_once) return { media: [], rest: attachments };
  const isMedia = (a) => a.kind === 'image' || a.kind === 'video';
  return { media: attachments.filter(isMedia), rest: attachments.filter(a => !isMedia(a)) };
}

// Photos and videos of a message: one large tile, or a grid of squares
// The grid gets a fixed width: tiles size from it (a shrink-to-fit grid would grow to the photo's
// natural width and leave the tile cut off on one side). Portrait photos are a bit narrower.
export function MediaGrid({ media }) {
  const single = media.length === 1;
  const ratio = single && media[0].width && media[0].height ? media[0].width / media[0].height : 1;
  const width = !single ? 260 : ratio < 1 ? 240 : 280;
  return (
    <div className={`grid max-w-full gap-0.5 overflow-hidden rounded-[18px] ${single ? 'grid-cols-1' : 'grid-cols-2'}`} style={{ width }}>
      {media.map(a => <MediaTile key={a.id} a={a} single={single} />)}
    </div>
  );
}

// Voice, audio, files and view-once media, shown inside the bubble
export function BubbleAttachments({ attachments, isMe, meId, canOpenViewOnce }) {
  return (
    <div className="flex flex-col gap-1.5">
      {attachments.map(a => (a.view_once
        ? <ViewOnce key={a.id} a={a} isMe={isMe} meId={meId} canOpen={canOpenViewOnce} />
        : a.kind === 'voice' || a.kind === 'audio'
          ? <AudioAttachment key={a.id} a={a} isMe={isMe} />
          : <FileAttachment key={a.id} a={a} isMe={isMe} />))}
    </div>
  );
}

function MediaTile({ a, single }) {
  const { url: thumb } = useAttachmentUrl(a.id, { thumb: true, enabled: a.has_thumb });
  const [open, setOpen] = useState(false);
  const [play, setPlay] = useState(false);
  const tile = useRef();
  const { url: full } = useAttachmentUrl(a.id, { enabled: open || play });
  const ratio = a.width && a.height ? a.width / a.height : 4 / 3;
  const style = single
    ? { width: '100%', aspectRatio: String(Math.min(Math.max(ratio, 0.6), 2)) }
    : { aspectRatio: '1' };

  if (a.kind === 'video' && play) {
    return full
      ? <video src={full} controls autoPlay playsInline className="block max-h-[360px] w-full bg-black" />
      : <div className="flex items-center justify-center bg-surface-3" style={style}><Spinner /></div>;
  }
  return (
    <>
      <button
        ref={tile}
        type="button"
        onClick={() => (a.kind === 'video' ? setPlay(true) : setOpen(true))}
        aria-label={a.kind === 'video' ? `Play video ${a.name}` : `Open photo ${a.name}`}
        className="relative block cursor-pointer overflow-hidden rounded-none border-0 bg-surface-3 p-0"
        style={style}
      >
        {thumb ? <img src={thumb} alt="" className="block h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center"><Spinner /></span>}
        {a.kind === 'video' && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full text-white" style={{ background: 'rgba(0,0,0,0.55)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>
            </span>
            {a.duration_ms != null && (
              <span className="absolute bottom-1.5 right-2 rounded px-1.5 text-[11px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.55)' }}>{formatDuration(a.duration_ms)}</span>
            )}
          </span>
        )}
      </button>
      {open && full && <ImageLightbox src={full} originRef={tile} onClose={() => setOpen(false)} />}
    </>
  );
}

function AudioAttachment({ a, isMe }) {
  const [load, setLoad] = useState(false);
  const { url } = useAttachmentUrl(a.id, { enabled: load });
  const voice = a.kind === 'voice';
  return (
    <div className="flex min-w-[220px] items-center gap-2.5">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isMe ? 'text-white' : 'bg-purple-glow text-purple-light'}`} style={isMe ? { background: 'rgba(255,255,255,0.18)' } : undefined}>
        {voice
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" /></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>}
      </span>
      {url ? (
        <audio src={url} controls autoPlay className="h-9 min-w-0 flex-1" />
      ) : (
        <button type="button" onClick={() => setLoad(true)} className={`flex min-w-0 flex-1 cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left text-sm ${isMe ? 'text-white' : 'text-text'}`}>
          {load ? <Spinner /> : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>}
          <span className="truncate">{voice ? 'Voice message' : a.name}</span>
          <span className="ml-auto shrink-0 text-xs opacity-70">{formatDuration(a.duration_ms)}</span>
        </button>
      )}
    </div>
  );
}

function FileAttachment({ a, isMe }) {
  const source = useSource();
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const url = await fetchBlobUrl(source, a.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.name || 'file';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {}
    setBusy(false);
  };
  return (
    <button
      type="button"
      onClick={save}
      title={`Download ${a.name}`}
      aria-label={`Download ${a.name}, ${formatSize(a.size)}`}
      className={`flex min-w-[200px] max-w-full cursor-pointer items-center gap-2.5 rounded-lg border-0 px-2.5 py-2 text-left ${isMe ? 'text-white' : 'text-text'}`}
      style={{ background: isMe ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)' }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{a.name}</span>
        <span className="block text-xs opacity-70">{formatSize(a.size)}</span>
      </span>
      {busy ? <Spinner /> : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 opacity-80"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
      )}
    </button>
  );
}

/* ── View once ── */

function ViewOnce({ a, isMe, meId, canOpen }) {
  const [viewing, setViewing] = useState(false);
  const [done, setDone] = useState(false);
  const label = a.kind === 'video' ? 'video' : 'photo';
  const openedByMe = done || (a.viewed_by || []).includes(meId);

  let text;
  let action = null;
  if (isMe) {
    text = (a.viewed_by || []).length ? `View-once ${label} · Opened` : `View-once ${label}`;
  } else if (openedByMe) {
    text = 'Opened';
  } else if (!canOpen || !a.available) {
    text = `View-once ${label}`;
  } else {
    text = `View ${label}`;
    action = () => setViewing(true);
  }

  return (
    <>
      <button
        type="button"
        disabled={!action}
        onClick={action || undefined}
        className={`flex min-w-[180px] items-center gap-2.5 rounded-lg border-0 px-3 py-2.5 text-left text-sm font-semibold ${action ? 'cursor-pointer' : 'cursor-default'} ${isMe ? 'text-white' : 'text-text'}`}
        style={{ background: isMe ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)' }}
      >
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-dashed text-xs font-black ${openedByMe || isMe ? 'opacity-60' : ''}`} style={{ borderColor: 'currentColor' }}>1</span>
        <span className={openedByMe ? 'opacity-60' : ''}>{text}</span>
      </button>
      {viewing && <ViewOnceViewer a={a} onClose={() => { setViewing(false); setDone(true); }} />}
    </>
  );
}

// Fullscreen, one time: the server deletes the file once every recipient has opened it
function ViewOnceViewer({ a, onClose }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let objectUrl = null;
    // Not cached: it can only be loaded once anyway
    axios.get(attachmentUrl(a.id), { headers: authHeaders(), responseType: 'blob' })
      .then(res => { objectUrl = URL.createObjectURL(res.data); setUrl(objectUrl); })
      .catch(err => setError(err.response?.status === 410 ? 'This was already opened.' : 'Could not open it.'));
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('keydown', key); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [a.id, onClose]);

  return createPortal(
    <Motion {...presets.fade} className="fixed inset-0 z-[700] flex flex-col items-center justify-center bg-black p-4" onClick={onClose} data-noswipe>
      <div className="mb-3 text-center text-xs text-text-dim">View once · Screenshots can't be prevented</div>
      <div onClick={e => e.stopPropagation()} className="flex max-h-[80vh] max-w-full items-center justify-center">
        {error ? <div className="text-sm text-text-muted">{error}</div>
          : !url ? <Spinner />
            : a.kind === 'video'
              ? <video src={url} autoPlay controls playsInline controlsList="nodownload" className="max-h-[80vh] max-w-full" />
              : <img src={url} alt="" className="max-h-[80vh] max-w-full select-none object-contain" draggable={false} onContextMenu={e => e.preventDefault()} />}
      </div>
      <button type="button" onClick={onClose} className="mt-4 cursor-pointer rounded-pill border-0 bg-surface-3 px-6 py-2 font-display text-sm font-bold text-text">Close</button>
    </Motion>,
    document.body
  );
}

function Spinner() {
  return <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-border border-t-purple" />;
}
